import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme"; // Store Custom Code
import { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities"; // Store Custom Code
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS } from "@store/theme/design"; // Store Custom Code
import { useEmblaCarousel } from "embla-carousel/react";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface SlideModel {
    fields: {
        slideTitle: string;
        background: "image" | "color";
        image: string;
        imageMobile: string; // Store Custom Code
        imageTablet: string; // Store Custom Code
        imageOverlay: string;
        partialOverlay: boolean;
        partialOverlayPositioning: "top" | "middle" | "bottom";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        heading: string;
        subheading: string;
        buttonLabel: string;
        hideButton: boolean; // Store Custom Code
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        focalPoint:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        contentPadding: number;
        centerTextVertically: boolean;
    };
}

const enum fields {
    height = "height",
    extendMobileWidth = "extendMobileWidth", // Store Custom Code
    desktopRatioHeight = "desktopRatioHeight", // Store Custom Code
    desktopRatioWidth = "desktopRatioWidth", // Store Custom Code
    tabletRatioHeight = "tabletRatioHeight", // Store Custom Code
    tabletRatioWidth = "tabletRatioWidth", // Store Custom Code
    mobileRatioHeight = "mobileRatioHeight", // Store Custom Code
    mobileRatioWidth = "mobileRatioWidth", // Store Custom Code
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport" | "manual"; // Store Custom Code
        [fields.extendMobileWidth]: boolean; // Store Custom Code
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean; // Store Custom Code
        [fields.autoplay]: number; // Store Custom Code
        [fields.desktopRatioHeight]: number; // Store Custom Code
        [fields.desktopRatioWidth]: number; // Store Custom Code
        [fields.tabletRatioHeight]: number; // Store Custom Code
        [fields.tabletRatioWidth]: number; // Store Custom Code
        [fields.mobileRatioHeight]: number; // Store Custom Code
        [fields.mobileRatioWidth]: number; // Store Custom Code
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.slides]: SlideModel[];
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
}

export const slideshowStyles: SlideshowStyles = {
    // Store Custom Code
    slideshowWrapper: {
        css: css`
            position: relative;
            padding: 10px 0;
            margin-bottom: 60px;
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
        `,
    },
    prevArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    iconProps: {
        size: 35,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: white;
            background-repeat: no-repeat;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
        `,
    },
    // Store Custom Code
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            position: absolute;
            width: 100%;
        `,
    },
    // Store Custom Code
    dotButton: {
        css: css`
            height: 10px !important;
            min-height: auto !important;
            max-height: 10px !important;
            width: 60px;
            padding: 0;
            margin: 25px 4px;
            border: 0;
            border-radius: 0;
            background: ${COLORS.border.dark};
            &:hover,
            &:active {
                background: ${COLORS.border.dark};
            }
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
            right: 0;
        `,
    },
    nextArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}

.slide-arrow {
}
`;

export const Slideshow = ({ fields, extendedStyles }: OwnProps) => {
    const { isInShell } = useShellContext();
    const history = useHistory();
    const buttonLinks = useGetLinks(fields.slides, o => o.fields.buttonLink);
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, fields.slides]);

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            // Store Custom Code - Allow client to link to his other websites
            const urlPatternPattern = /^((http|https|ftp):\/\/)/;
            if (urlPatternPattern.test(url)) {
                global.window.location.href = url;
            } else {
                history.push(url);
            }
        }
    };

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    // Store Custom Code: Add option to manually choose ratio
    let heightStyles: string;
    if (fields.height !== "manual") {
        switch (fields.height) {
            case "1/4 viewport":
                heightStyles = "min-height: 25vh;";
                break;
            case "1/2 viewport":
                heightStyles = "min-height: 50vh;";
                break;
            case "3/4 viewport":
                heightStyles = "min-height: 75vh;";
                break;
            case "fullViewport":
                heightStyles = "min-height: 100vh;";
                break;
        }
    }
    // Store Custom Code: Add option to overflow outside container on mobile
    let wrapperStyle: InjectableCss;
    if (fields.extendMobileWidth) {
        wrapperStyle = {
            css: css`
                ${styles?.slideshowWrapper?.css}
                ${({ theme }: { theme: BaseTheme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                width: 105%;
                                margin-left: -2.5%;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            `,
        };
    } else {
        wrapperStyle = {
            css: css`
                ${styles?.slideshowWrapper?.css}
            `,
        };
    }
    const textAlignStyles = `text-align: ${fields.textAlignment};`;

    // Store Custom Code
    return (
        <StyledWrapper {...wrapperStyle}>
            {fields.showArrows && fields.slides.length > 1 && (
                <StyledWrapper {...styles.prevArrowWrapper}>
                    <Button
                        {...styles.prevArrowButton}
                        onClick={() => embla && embla.scrollPrev()}
                        aria-label={translate("Previous")}
                    >
                        <ButtonIcon {...styles.iconProps} src={ChevronLeft} />
                    </Button>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                <StyledWrapper {...styles.slideContainerWrapper}>
                    {fields.slides.map((slide, index) => {
                        const buttonLink = buttonLinks[index];

                        let responsiveImageBehaviorStyles;
                        if (slide.fields.responsiveImageBehavior === "cover") {
                            responsiveImageBehaviorStyles = "background-size: cover;";
                        } else if (slide.fields.responsiveImageBehavior === "center") {
                            responsiveImageBehaviorStyles = "background-size: contain;";
                        } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
                            responsiveImageBehaviorStyles = "background-size: auto 100%;";
                        } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
                            responsiveImageBehaviorStyles = "background-size: 100% auto;";
                        }

                        // Store Custom Code: Remove backgroundStyles chunk
                        const focalPointStyles = getFocalPointStyles(slide.fields.focalPoint);

                        // Store Custom Code: Change the style declaration so it works with absolute position content
                        let overlayPositioningStyles;
                        if (!slide.fields.partialOverlay) {
                            overlayPositioningStyles = "";
                        } else if (slide.fields.partialOverlayPositioning === "top") {
                            overlayPositioningStyles = "top: 0;bottom: inherit;";
                        } else if (slide.fields.partialOverlayPositioning === "middle") {
                            overlayPositioningStyles = "top:inherit;bottom:50%;transform: translateY(50%);";
                        } else if (slide.fields.partialOverlayPositioning === "bottom") {
                            overlayPositioningStyles = "top:inherit;bottom: 0;";
                        }

                        let fontSizeStyles;
                        if (fields.responsiveFontSizes || fields.customFontSizes) {
                            fontSizeStyles = responsiveStyleRules(
                                fields.responsiveFontSizes,
                                fields.customFontSizes ? fields : undefined,
                            );
                        }

                        // Store Custom Code - Add manual and ratio mode for responsive styles
                        const slideWrapperStyles = {
                            css: css`
                                ${styles.slideContentWrapper?.css || ""}
                                ${fields.height !== "manual" ? heightStyles : ""}
                                ${textAlignStyles}
                                ${responsiveImageBehaviorStyles}
                                ${focalPointStyles}
                                ${fontSizeStyles}
                                ${({ theme }: { theme: BaseTheme }) => {
                                    if (slide.fields.background === "image") {
                                        if (slide.fields.imageMobile !== "" && slide.fields.imageTablet !== "") {
                                            return breakpointMediaQueries(
                                                theme,
                                                [
                                                    css`
                                                        background-image: url(${parseImageUrl(slide.fields.imageMobile)
                                                            .url});
                                                    `,
                                                    css`
                                                        background-image: url(${parseImageUrl(slide.fields.imageTablet)
                                                            .url});
                                                    `,
                                                    css`
                                                        background-image: url(${parseImageUrl(slide.fields.image).url});
                                                    `,
                                                ],
                                                "min",
                                            );
                                        }
                                        return `background-image: url(${parseImageUrl(slide.fields.image).url});`;
                                    }
                                    return `background-color: ${slide.fields.backgroundColor};`;
                                }}
                                ${({ theme }: { theme: BaseTheme }) => {
                                    if (fields.height === "manual") {
                                        return breakpointMediaQueries(
                                            theme,
                                            [
                                                css`
                                                    padding: ${100 /
                                                        (fields.mobileRatioWidth / fields.mobileRatioHeight)}%
                                                        0 0 0;
                                                `,
                                                css`
                                                    padding: ${100 /
                                                        (fields.tabletRatioWidth / fields.tabletRatioHeight)}%
                                                        0 0 0;
                                                `,
                                                css`
                                                    padding: ${100 /
                                                        (fields.desktopRatioWidth / fields.desktopRatioHeight)}%
                                                        0 0 0;
                                                `,
                                            ],
                                            "min",
                                        );
                                    }
                                }}}
                                position: relative;
                                cursor: ${slide.fields.hideButton ? "pointer" : "default"};
                                &:hover{
                                    transition: opacity .15s ease-in-out;
                                    opacity: ${slide.fields.hideButton ? ".8" : "1"};
                                    ${slide.fields.hideButton ? `outline: 2px solid ${COLORS.primary.base};` : ""}
                                }
                                ${
                                    !slide.fields.partialOverlay
                                        ? `&:after {
                                        content: "";
                                        display: block;
                                        position: absolute;
                                        top: 0;
                                        bottom: 0;
                                        right: 0;
                                        left: 0;
                                        background-color: ${
                                            slide.fields.background === "image" ? slide.fields.imageOverlay : ""
                                        };
                                        z-index: 1;
                                    }`
                                        : ""
                                }
                                
                            `,
                        };
                        // Store Custom Code
                        const slideOverlayWrapperStyles = {
                            css: css`
                                ${styles.slideOverlayWrapper?.css || ""}
                                padding: ${slide.fields.contentPadding}px 0; // Store Custom Code
                                position: absolute;
                                bottom: 0;
                                z-index: 2;
                                ${slide.fields.partialOverlay
                                    ? `
                                        ${overlayPositioningStyles}
                                        background-color: ${
                                            slide.fields.background === "image" ? slide.fields.imageOverlay : ""
                                        };`
                                    : ""}
                                ${slide.fields.centerTextVertically &&
                                `top:inherit;bottom:50%;
                                    transform: translateY(50%);`}
                            `,
                        };

                        return (
                            // Store Custom Code: Custom click behavior when no btns
                            <StyledWrapper
                                className="slide-wrapper"
                                onClick={() => {
                                    if (slide.fields.hideButton) {
                                        return onClick(buttonLink?.url);
                                    }
                                }}
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                {...slideWrapperStyles}
                            >
                                <StyledWrapper {...slideOverlayWrapperStyles}>
                                    <StyledWrapper {...styles.slideCenteringWrapperStyles}>
                                        {slide.fields.heading && (
                                            <Typography className="slide-heading" {...styles.headingText}>
                                                {parse(slide.fields.heading, parserOptions)}
                                            </Typography>
                                        )}
                                        {slide.fields.subheading && (
                                            <Typography className="slide-subheading" {...styles.subheadingText}>
                                                {parse(slide.fields.subheading, parserOptions)}
                                            </Typography>
                                        )}
                                        {/* Store Custom Code: Custom feature to hide btns */}
                                        {!slide.fields.hideButton &&
                                            (slide.fields.buttonLabel || slide.fields.buttonLink.value) && (
                                                <Button
                                                    className="slide-button"
                                                    {...styles.slideButton}
                                                    variant={slide.fields.buttonVariant}
                                                    onClick={() => onClick(buttonLink?.url)}
                                                >
                                                    {slide.fields.buttonLabel || buttonLink?.title || buttonLink?.url}
                                                </Button>
                                            )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            </StyledWrapper>
            {fields.slideIndicator && (
                <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                    {fields.slides.map((_, i) => {
                        const dotColorStyles =
                            i === selectedIndex
                                ? `
                                        background: white;
                                        &:hover { background: white; }
                                    `
                                : "";
                        const dotButtonStyles = {
                            css: css`
                                ${styles.dotButton?.css || ""}
                                ${dotColorStyles}
                            `,
                        };

                        return (
                            <Button
                                className="slide-dot-button"
                                // eslint-disable-next-line react/no-array-index-key
                                key={i}
                                {...dotButtonStyles}
                                onClick={() => embla && embla.scrollTo(i)}
                                aria-label={translate("Scroll to {0}", `${i + 1}`)}
                            />
                        );
                    })}
                </StyledWrapper>
            )}
            {fields.showArrows && fields.slides.length > 1 && (
                <StyledWrapper {...styles.nextArrowWrapper}>
                    <Button
                        {...styles.nextArrowButton}
                        onClick={() => embla && embla.scrollNext()}
                        aria-label={translate("Next")}
                    >
                        <ButtonIcon {...styles.iconProps} src={ChevronRight} />
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

// Store Custom Code: Add custom fields
const widgetModule: WidgetModule = {
    component: Slideshow,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                    { displayName: "Manual", value: "manual" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            // Store Custom Code
            {
                name: "desktopRatioWidth",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 47,
                fieldType: "General",
                tab: settingsTab,
                isVisible: (item?: HasFields) => {
                    return item?.fields.height === "manual";
                },
            },
            {
                name: "desktopRatioHeight",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 13,
                fieldType: "General",
                tab: settingsTab,
                isVisible: (item?: HasFields) => {
                    return item?.fields.height === "manual";
                },
            },
            {
                name: "tabletRatioWidth",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 43,
                fieldType: "General",
                tab: settingsTab,
                isVisible: (item?: HasFields) => {
                    return item?.fields.height === "manual";
                },
            },
            {
                name: "tabletRatioHeight",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 50,
                fieldType: "General",
                tab: settingsTab,
                isVisible: (item?: HasFields) => {
                    return item?.fields.height === "manual";
                },
            },
            {
                name: "mobileRatioWidth",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 43,
                fieldType: "General",
                tab: settingsTab,
                isVisible: (item?: HasFields) => {
                    return item?.fields.height === "manual";
                },
            },
            {
                name: "mobileRatioHeight",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 50,
                fieldType: "General",
                tab: settingsTab,
                isVisible: (item?: HasFields) => {
                    return item?.fields.height === "manual";
                },
            },
            {
                name: fields.extendMobileWidth,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],

                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "image",
                        displayName: "Image (Default - Desktop)",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageTablet",
                        displayName: "Image Tablet",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageMobile",
                        displayName: "Image Mobile",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageOverlay",
                        displayName: "Image Color Overlay",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "partialOverlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "partialOverlayPositioning",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top", value: "top" },
                            { displayName: "Middle", value: "middle" },
                            { displayName: "Bottom", value: "bottom" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "bottom",
                        isVisible: widget => widget.fields.partialOverlay,
                    },
                    {
                        name: "responsiveImageBehavior",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Cover", value: "cover" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Prioritize height", value: "prioritizeHeight" },
                            { displayName: "Prioritize width", value: "prioritizeWidth" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "cover",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "black",
                        isVisible: widget => widget.fields.background === "color",
                    },
                    {
                        name: "focalPoint",
                        displayName: "Focal Point",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top Left", value: "topLeft" },
                            { displayName: "Top Center", value: "topCenter" },
                            { displayName: "Top Right", value: "topRight" },
                            { displayName: "Center Left", value: "centerLeft" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Center Right", value: "centerRight" },
                            { displayName: "Bottom Left", value: "bottomLeft" },
                            { displayName: "Bottom Center", value: "bottomCenter" },
                            { displayName: "Bottom Right", value: "bottomRight" },
                        ],
                        defaultValue: "center",
                        hideEmptyOption: true,
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "hideButton",
                        displayName: "Hide button and make all slide clickable",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "IntegerField",
                        defaultValue: 50,
                    },
                    {
                        name: "centerTextVertically",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
